/**
 * Lucide → Cesium billboard pipeline (Phase V.3 §1).
 *
 * Cesium billboards consume raster images; Lucide ships icons as SVG primitive
 * node arrays (`lucide-static/icon-nodes.json`). This module rasterises those
 * nodes to a PNG data-URL via an offscreen 2D canvas, parameterised by colour,
 * size, stroke width and rotation, and caches the result so repeat renders are
 * free (no flicker at high marker density).
 *
 * The rasteriser is injectable (`__setRasterizerForTest`) so the cache / key /
 * eviction logic is unit-testable without a real canvas (happy-dom has none).
 * Actual pixel fidelity is verified in-browser (CHECKPOINT 1).
 *
 * This is the foundation EVERY V.3 marker uses — aircraft, response assets,
 * Ontario Corps category markers all resolve their glyph through here.
 */
import iconNodes from 'lucide-static/icon-nodes.json';
import {
  CAHS_GLYPHS,
  CAHS_GLYPH_META,
  type CahsCategory,
  type CahsGlyphKey,
} from './assets/cahs/index.generated';
import { resolveCahsInk } from './cahsRenderMode';
import { tintedSvgDataUrl } from './svgIcon';

type IconNode = [string, Record<string, string | number>];

const LUCIDE_NODES: Record<string, IconNode[]> = iconNodes;

/**
 * Extensibility hook for glyphs Lucide 0.383 doesn't ship, authored in Lucide's
 * 24×24 stroke-only convention (fill:none, round caps/joins) so they rasterise
 * identically to the real icons — they ride the SAME canvas pipeline (and so
 * return null without a canvas, letting callers fall back to a coloured point in
 * unit tests, exactly like a real Lucide glyph).
 */
const CUSTOM_NODES: Record<string, IconNode[]> = {
  /**
   * #351 — the community marker glyph (operator-cop-doctrine §3B.8 ambient
   * register). Founder ruling 2026-06-18: ONE community symbol, no FN/town split.
   * Lucide 0.383 ships no 3-figure "group of people" glyph (`users` is 2 figures
   * AND is already the volunteer_chapter / OC search_rescue asset glyph — reusing
   * it would collide two distinct entity types on the COP), so this is an original
   * stroke-only glyph: a prominent centre figure flanked by two smaller ones, so it
   * reads unambiguously as "a community of people" at the 20px ambient size and is
   * distinct from the old `home` glyph (which read as a single building). Three
   * figures = head circle + open shoulder arc each (drawing order side-then-centre
   * so the centre figure sits in front). Fill is tinted to the wellness-check status
   * by the caller (§3B.8) — never animates.
   */
  'community-group': [
    // left figure (smaller, behind)
    ['circle', { cx: 5.5, cy: 9.5, r: 2.3 }],
    ['path', { d: 'M2.2 20v-1a3.3 3.3 0 0 1 6.6 0v1' }],
    // right figure (smaller, behind)
    ['circle', { cx: 18.5, cy: 9.5, r: 2.3 }],
    ['path', { d: 'M15.2 20v-1a3.3 3.3 0 0 1 6.6 0v1' }],
    // centre figure (prominent, front)
    ['circle', { cx: 12, cy: 8, r: 2.9 }],
    ['path', { d: 'M7.5 20v-1.6a4.5 4.5 0 0 1 9 0V20' }],
  ],
};

/** #351 — the community marker glyph name (see {@link CUSTOM_NODES}). Exported so
 *  the communities layer names one symbol in one place. */
export const COMMUNITY_GLYPH = 'community-group';

function lookupNodes(name: string): IconNode[] | null {
  return CUSTOM_NODES[name] ?? LUCIDE_NODES[name] ?? null;
}

// --- CAHS clean-room glyph source (#303/#304) -------------------------------
/**
 * CAHS-style glyphs are SOLID monochrome single-path SVGs (`currentColor` ink),
 * NOT stroke-only Lucide node arrays — so they ride the solid-SVG retint
 * pipeline (`svgIcon.tintedSvgDataUrl`: `currentColor` → token colour, pure
 * string work, canvas-free, works in tests) rather than the Lucide canvas
 * rasteriser above. They register here BESIDE {@link CUSTOM_NODES} as a second
 * glyph source, addressed under the `cahs:` namespace (e.g. `cahs:flood`), so
 * `renderLucideToDataUrl('cahs:flood', { color })` stays the single public
 * entry point every marker family already calls (design ruling PART 4). The
 * family token resolver supplies `color`, so lifecycle recolour + the ambient/
 * act registers come for free; the {@link resolveCahsInk} seam drops the hue in
 * monochrome / gov-compliance mode (#308). Generated — see
 * scripts/gen-cahs-icons.mjs + assets/cahs/index.generated.ts.
 */
export const CAHS_GLYPH_PREFIX = 'cahs:';

/** The namespaced registry name for a CAHS glyph key (`flood` → `cahs:flood`). */
export function cahsGlyphName(key: CahsGlyphKey): string {
  return `${CAHS_GLYPH_PREFIX}${key}`;
}

/** Raw SVG for a `cahs:`-prefixed name, or null if it is not a CAHS glyph. */
function lookupCahsGlyph(name: string): string | null {
  if (!name.startsWith(CAHS_GLYPH_PREFIX)) return null;
  const key = name.slice(CAHS_GLYPH_PREFIX.length);
  return (CAHS_GLYPHS as Record<string, string>)[key] ?? null;
}

/**
 * Add a contrasting outline to a clean-room CAHS glyph (#384). The glyphs are
 * solid `fill="currentColor"` paths; we stamp each filled element with a stroke
 * painted BEHIND the fill (`paint-order="stroke"`) so it shows as a thin halo
 * around the silhouette without eating its interior — the standard outlined-glyph
 * technique. The stroke `color` is a LITERAL (not `currentColor`), so the
 * downstream `currentColor`→ink retint leaves it untouched: the fill recolours
 * with the lifecycle/mono ink while the dark edge stays constant. `width` is in
 * the glyph's 0–100 viewBox units. Rounded joins/caps keep the edge clean at
 * marker scale. Idempotent string work (no canvas), so it renders in tests.
 */
function withGlyphOutline(rawSvg: string, color: string, width: number): string {
  const strokeAttrs = `stroke="${color}" stroke-width="${width}" paint-order="stroke" stroke-linejoin="round" stroke-linecap="round"`;
  return rawSvg.replace(/fill="currentColor"/g, `fill="currentColor" ${strokeAttrs}`);
}

// --- CAHS framed asset glyph (#305 Wave-2, council PART 2.2) ----------------
/**
 * Assets/infrastructure adopt the CAHS glyph WITH its category FRAME (PART 2.2):
 * the ▭ infrastructure / ○ operations frame is a SECOND monochrome encoder — it
 * reads the category with zero colour. (Hazards omit the frame; that is Wave-1's
 * {@link ../layers/anomalyMarker.hazardGlyphBillboard}.)
 *
 * #412: the asset glyphs are now the real-Crown EXACT art, which BAKES its category
 * frame into the path — so this mirrors the act-chip geometry ({@link actChipCompositeSvg})
 * rather than drawing its own frame: a category-frame-shaped BACKING (filled with the
 * literal `backing` ink, scaled ~{@link CHIP_BACKING_SCALE} for a thin keyline) sits
 * UNDER the FULL-SIZE glyph, whose own baked frame IS the visible ring — no separate
 * outline, no inset (feeding baked-frame art the old drawn-frame + 0.62-inset path
 * double-framed it). The frame shape is derived from the glyph's own category in
 * {@link CAHS_GLYPH_META} (one rules table → zero drift). The composite is built ONCE
 * and ridden through the SAME `cahs:` retint as the single glyph ({@link resolveCahsInk}
 * + {@link tintedSvgDataUrl}): the glyph inks `currentColor` → the resolved ambient
 * token (so the #308 monochrome / gov-compliance seam applies for free), while the
 * chip BACKING is a fixed neutral fill that never carries a register hue. STATIC — a
 * CAHS glyph never animates (Law 6).
 */
const CATEGORY_FRAME: Record<CahsCategory, CahsGlyphKey> = {
  incident: 'frame-incident',
  infrastructure: 'frame-infrastructure',
  operations: 'frame-operations',
};

/** Inner markup of a single-`<svg>` CAHS glyph (its `<path>`, for an EXACT glyph
 *  wrapped in its positioning `<g transform>`). */
function cahsInnerMarkup(rawSvg: string): string {
  const m = rawSvg.match(/<svg[^>]*>([\s\S]*)<\/svg>/);
  return m ? m[1]! : '';
}

/** The first path's `d` in a frame SVG — reused, filled, as the chip backing. */
function cahsFramePathD(rawSvg: string): string | null {
  const m = rawSvg.match(/\bd="([^"]+)"/);
  return m ? m[1]! : null;
}

/** Cache of the pre-tint composite SVG string, keyed by `${glyph}|${backing}`. */
const framedCompositeCache = new Map<string, string | null>();

/** Build (memoised) the untinted ambient composite SVG, mirroring the act-chip
 *  geometry ({@link actChipCompositeSvg}): the category-frame shape filled with
 *  `backing` (scaled about centre by {@link CHIP_BACKING_SCALE} for a thin keyline)
 *  UNDER the full-size EXACT glyph, whose own baked frame is the visible ring
 *  (currentColor → ambient/mono ink downstream). Returns null if the glyph or its
 *  category frame is unknown. */
function framedCompositeSvg(glyph: CahsGlyphKey, backing: string): string | null {
  const cacheKey = `${glyph}|${backing}`;
  const cached = framedCompositeCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const meta = CAHS_GLYPH_META[glyph];
  const glyphRaw = (CAHS_GLYPHS as Record<string, string>)[glyph];
  const frameKey = meta ? CATEGORY_FRAME[meta.category] : undefined;
  const frameRaw = frameKey ? (CAHS_GLYPHS as Record<string, string>)[frameKey] : undefined;
  if (!glyphRaw || !frameRaw) {
    framedCompositeCache.set(cacheKey, null);
    return null;
  }
  const frameD = cahsFramePathD(frameRaw);
  const glyphInner = cahsInnerMarkup(glyphRaw);
  if (!frameD || !glyphInner) {
    framedCompositeCache.set(cacheKey, null);
    return null;
  }
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 100 100">` +
    // ambient chip backing — the category-frame shape filled with the (literal)
    // backing ink, scaled a hair past the EXACT glyph's baked frame so a thin
    // keyline rings it (shares CHIP_BACKING_SCALE with the act chip).
    `<g transform="translate(50 50) scale(${CHIP_BACKING_SCALE}) translate(-50 -50)">` +
    `<path d="${frameD}" fill="${backing}"/></g>` +
    // the EXACT glyph at full size on top — its OWN baked frame is the visible ring
    // (no separate outline, no inset); currentColor so resolveCahsInk retints it.
    glyphInner +
    `</svg>`;
  framedCompositeCache.set(cacheKey, svg);
  return svg;
}

/**
 * Render a CAHS asset glyph inside its category frame as one tinted data-URL — the
 * ambient-register asset marker (council PART 2.2/2.3). `color` is the ambient ink
 * the glyph (its baked frame ring + symbol) carries; `backing` is the fixed chip
 * fill behind it. The ink resolves through {@link resolveCahsInk} so the #308 mono
 * seam drops the hue automatically; the backing is a literal, untouched by the
 * retint. Canvas-free (pure string work), so it renders identically in unit tests.
 * Returns null if the glyph (or its category frame) is unknown — callers fall back
 * to a plain point.
 */
export function renderCahsFramedGlyph(
  glyph: CahsGlyphKey,
  options: { color?: string; backing: string; mono?: boolean },
): string | null {
  const composite = framedCompositeSvg(glyph, options.backing);
  if (composite === null) return null;
  const ink = resolveCahsInk(options.color ?? DEFAULTS.color, options.mono);
  return tintedSvgDataUrl(`cahs-framed:${glyph}:${options.backing}`, composite, ink);
}

// --- CAHS act-register CHIP (#411, icon-design-council Follow-up RESULT) ------
/**
 * The CHIP replaces the #384 white-knockout for a hazard glyph riding INSIDE the
 * red act dot. Compositing order (Follow-up RESULT §1): the EXACT glyph's own
 * category-frame shape, filled OPAQUE WHITE (the chip backing) → the DARK glyph on
 * top. The real-Crown EXACT glyph bakes its category frame into the art (e.g. an
 * incident glyph carries its ◇ ring), so the dark frame ring lands on the white
 * chip's edge and the dark symbol reads on the white interior — fixing the
 * knockout's two failures (the dark contrast-well band, and red bleeding through
 * the glyph's open marks).
 *
 * It is hue-free BY CONSTRUCTION — white backing + dark symbol are both fixed
 * LITERALS, so the chip is ONE static image per glyph, identical on URGENT-RED,
 * ACK-STEEL, and the cleared ghost; the DOT underneath carries the whole lifecycle
 * hue. That is the "lifecycle-robust" property, and it also makes the chip correct
 * in #308 monochrome mode for free (neutral white/dark carry no token hue to drop).
 *
 * NOT a reversal of Ruling 2 ("keep the circle"): the act DOT stays a CIRCLE; the
 * chip is a small white category-frame BACKING that rides inside it (the red signal
 * still rings the chip). STATIC — a CAHS glyph never animates (Law 6).
 */
export const CHIP_BACKING_INK = '#ffffff'; // opaque white chip field (a literal — never retinted)
export const CHIP_SYMBOL_INK = '#06121c'; // the dark symbol on the chip (the #384 keyline literal)
/** White backing scaled a hair past the EXACT glyph's baked frame (8-92 viewBox)
 *  so a thin white keyline rings the dark frame edge — matches the founder-approved
 *  EXACT contact sheet (a white chip the red bites around, dark ring just inside). */
export const CHIP_BACKING_SCALE = 1.06;
/** R1/#477 incident lifecycle 'owned'/'response' STRUCTURAL keyline (NOT a colour rim —
 *  the zone rim hue is taken). A DOUBLED white backing: an outer white plate
 *  ({@link CHIP_KEYLINE_OUTER_SCALE}) with a thin dark separator
 *  ({@link CHIP_KEYLINE_MID_SCALE}) below the standard {@link CHIP_BACKING_SCALE} plate
 *  → concentric white-keyline · dark-hairline · white-chip. A SHAPE cue (the chip
 *  visibly bulks), so it stays mono-correct (#308) + deuteranopia-safe and reuses the
 *  existing dark/white literals. Purely additive: the no-keyline path is byte-identical
 *  to the shipped hazard chip (the #472 parity test). */
export const CHIP_KEYLINE_OUTER_SCALE = 1.52;
export const CHIP_KEYLINE_MID_SCALE = 1.28;
/**
 * Symbol BULK (#470, W1 symbology fix-wave 2026-06-30) — stroke width, in the
 * glyph's LOCAL path units (every CAHS glyph is normalised at scale 1.6149, so a
 * fixed local width bulks them all uniformly: 1.5 → ≈2.4 viewBox units). The dark
 * symbol is fill="currentColor"; we add a SAME-INK `currentColor` stroke (NOT
 * paint-order — the chip test forbids it; a same-colour stroke just fattens the
 * silhouette outward) so the thin baked-frame ring and the intricate hazard mark
 * survive downsampling to the 14–26 px marker. WITHOUT this the symbol collapses
 * to mud at operator/regional zoom — the founder's "thin black symbol outline,
 * red showing through" defect. The retint substitutes BOTH fill and stroke
 * `currentColor` → the dark literal, so the chip stays hue-free + mono-correct.
 */
export const CHIP_SYMBOL_BULK = 1.5;

/** Zone-chip RIM stroke width (#475, W1 Slice 2 zone-legibility), in the glyph's
 *  0–100 viewBox units. A thin literal-colored stroke ringing the chip PLATE so the
 *  warning-zone's COLOR rides on the chip edge while the SHAPE (the dark symbol on
 *  the light plate) stays primary — the founder-signed 2026-06-30 rule (color on
 *  fill + thin rim, NOT glyph-tinted-to-fill which washes out per the 4-seat gate
 *  G8/G9). The rim is a LITERAL (untouched by the currentColor→symbol retint). */
export const ZONE_CHIP_RIM_WIDTH = 6;

/** A thin colored rim ringing a chip plate (#475). `color` is a LITERAL CSS colour
 *  (the warning-zone hue), so the downstream `currentColor`→symbol retint never
 *  touches it; `width` is in the glyph's 0–100 viewBox units. */
export interface ChipRim {
  color: string;
  width?: number;
}

/** Cache of the pre-tint chip composite SVG, keyed by glyph (+ optional rim). */
const actChipCompositeCache = new Map<string, string | null>();

/**
 * Bulk a CAHS glyph's inner markup (#470): give every solid `currentColor` path a
 * SAME-INK `currentColor` stroke so the silhouette fattens and the thin baked-frame
 * ring + intricate hazard mark survive downsampling to the 14–26 px marker. NOT
 * paint-order (the chip test forbids it, and same-colour fill+stroke needs no
 * ordering — the stroke just grows the outline). `width` is in the glyph's LOCAL
 * path units (all CAHS glyphs share the 1.6149 normalising scale). The retint
 * substitutes BOTH the fill and the new stroke `currentColor` → the symbol ink, so
 * the chip stays hue-free + mono-correct. Idempotent string work (no canvas).
 */
function bulkGlyphSilhouette(glyphInner: string, width: number): string {
  if (width <= 0) return glyphInner;
  return glyphInner.replace(
    /fill="currentColor"/g,
    `fill="currentColor" stroke="currentColor" stroke-width="${width}" stroke-linejoin="round" stroke-linecap="round"`,
  );
}

/** Build the untinted act-chip composite (#411/#470): a SOLID white category-frame
 *  PLATE (scaled about centre) under the full-size glyph, whose symbol is BULKED
 *  ({@link CHIP_SYMBOL_BULK}) so it reads as a crisp filled mark — not a hairline
 *  ring + hollow outline (the founder's every-zoom "red-through" defect). Pure
 *  string work; exported so the contact-sheet generator + its parity test bake the
 *  TRUE shipped composite (#472), not a stale mock. Returns null if the glyph or
 *  its category frame is unknown. */
export function actChipCompositeSvg(
  glyph: CahsGlyphKey,
  rim?: ChipRim,
  keyline?: boolean,
  glyphless?: boolean,
): string | null {
  // No-rim + no-keyline + with-glyph output stays BYTE-IDENTICAL to the shipped hazard
  // chip (the #472 parity test byte-mirrors this); rim (#475 zone), keyline (R1 lifecycle
  // 'owned'/'response'), and glyphless (R1 'detected' plate-only) are additive opt-in variants.
  const cacheKey =
    (rim ? `${glyph}|rim:${rim.color}:${rim.width ?? ZONE_CHIP_RIM_WIDTH}` : glyph) +
    (keyline ? '|keyline' : '') +
    (glyphless ? '|plate' : '');
  const cached = actChipCompositeCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const meta = CAHS_GLYPH_META[glyph];
  const glyphRaw = (CAHS_GLYPHS as Record<string, string>)[glyph];
  const frameKey = meta ? CATEGORY_FRAME[meta.category] : undefined;
  const frameRaw = frameKey ? (CAHS_GLYPHS as Record<string, string>)[frameKey] : undefined;
  if (!glyphRaw || !frameRaw) {
    actChipCompositeCache.set(cacheKey, null);
    return null;
  }
  const frameD = cahsFramePathD(frameRaw);
  const glyphInner = cahsInnerMarkup(glyphRaw);
  if (!frameD || !glyphInner) {
    actChipCompositeCache.set(cacheKey, null);
    return null;
  }
  // #475 zone variant only: a thin LITERAL-coloured rim on the plate edge (the zone
  // hue). Painted with the plate fill (default paint-order = stroke over the fill
  // edge) so it reads as a coloured border on the white chip; the literal survives
  // the currentColor→symbol retint.
  const rimAttrs = rim
    ? ` stroke="${rim.color}" stroke-width="${rim.width ?? ZONE_CHIP_RIM_WIDTH}" stroke-linejoin="round"`
    : '';
  // R1/#477 'owned'/'response' STRUCTURAL keyline: a DOUBLED white backing drawn
  // BEHIND the standard plate — outer white plate · dark separator hairline → reads as
  // a concentric white keyline ringing the chip. Same dark/white literals (no new hue),
  // so the symbol retint + mono-correctness are untouched. Empty string when off ⇒ the
  // default path is byte-identical.
  const keylineLayers = keyline
    ? `<g transform="translate(50 50) scale(${CHIP_KEYLINE_OUTER_SCALE}) translate(-50 -50)">` +
      `<path d="${frameD}" fill="${CHIP_BACKING_INK}"/></g>` +
      `<g transform="translate(50 50) scale(${CHIP_KEYLINE_MID_SCALE}) translate(-50 -50)">` +
      `<path d="${frameD}" fill="${CHIP_SYMBOL_INK}"/></g>`
    : '';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 100 100">` +
    keylineLayers +
    // SOLID white chip PLATE — the category frame shape, filled, scaled about centre.
    `<g transform="translate(50 50) scale(${CHIP_BACKING_SCALE}) translate(-50 -50)">` +
    `<path d="${frameD}" fill="${CHIP_BACKING_INK}"${rimAttrs}/></g>` +
    // the EXACT glyph at full size on top, its symbol BULKED (#470) so it survives
    // downsampling (its own baked frame ring + the hazard mark, dark on the plate).
    // glyphless ('detected') omits the symbol → a neutral white PLATE (no hazard commitment).
    (glyphless ? '' : bulkGlyphSilhouette(glyphInner, CHIP_SYMBOL_BULK)) +
    `</svg>`;
  actChipCompositeCache.set(cacheKey, svg);
  return svg;
}

/* Icon one-swing #564: renderCahsActChip (the hazard act-chip data-URL) was RETIRED —
 * every hazard family moved to the EXACT-ACT disc (discGlyphComposite.renderDiscGlyph),
 * so the chip's only consumer (anomalyMarker.hazardGlyphBillboard) is gone. The
 * underlying actChipCompositeSvg compositor SURVIVES: the zone centroid chip
 * (renderCahsZoneChip) + the framed-asset glyph geometry + the incident-lifecycle lock
 * test still bake it. */

/**
 * Render a warning-ZONE centroid identity chip data-URL (#475, W1 Slice 2) — the
 * shipped act-chip plate treatment (solid light plate + dark BULKED symbol, the
 * {@link actChipCompositeSvg} compositor) with a thin literal-coloured RIM ringing
 * the plate so the zone's COLOUR rides on the chip edge while the SHAPE stays
 * primary (founder-signed 2026-06-30: dark glyph on the light plate, colour on the
 * fill + a thin matching rim, NOT a glyph tinted to the same-hue fill — that washes
 * out per the 4-seat gate G8/G9). The dark symbol ink + white plate are fixed
 * literals; only the rim hue varies by zone, so the chip stays mono-correct (#308)
 * for free. Canvas-free (pure string work), so it renders identically in unit tests.
 * Returns null if the glyph (or its category frame) is unknown — callers fall back
 * to no glyph. See {@link actChipCompositeSvg} + {@link renderCahsActChip}.
 */
export function renderCahsZoneChip(glyph: CahsGlyphKey, rim: ChipRim): string | null {
  const composite = actChipCompositeSvg(glyph, rim);
  if (composite === null) return null;
  // Same as the act chip: the symbol ink is a fixed dark LITERAL (the dot/fill
  // underneath, not the symbol, carries the zone hue). tintedSvgDataUrl substitutes
  // the glyph's currentColor → dark; the white backing + the rim literal are untouched.
  return tintedSvgDataUrl(
    `cahs-zonechip:${glyph}:${rim.color}:${rim.width ?? ZONE_CHIP_RIM_WIDTH}`,
    composite,
    CHIP_SYMBOL_INK,
  );
}

// --- Operational symbol conventions (§1.2) ---------------------------------

/** Glyph pixel size by zoom tier — country (far) → local (close). */
export const ICON_SIZES = {
  country: 14,
  regional: 20,
  local: 28,
} as const;

/** Crisp, modern stroke. Matches Lucide's native 24-viewBox stroke weight. */
export const STROKE_WIDTH = 2;

/** Role accent colours (operational palette, Pinecrest north-star). */
export const STROKE_COLOR_BY_ROLE: Record<string, string> = {
  sar: '#fb923c', // orange
  water_bomber: '#f59f9a', // salmon (Colour Law 5 — off saturated red; lockstep w/ aircraftRoles)
  air_ambulance: '#3b82f6', // blue
  police: '#15803d', // deep green (Colour Law 5 — off LIVE-GREEN; lockstep w/ aircraftRoles)
  isr: '#eab308', // yellow
  fire_station: '#dc2626', // red
  aviation_base: '#94a3b8', // slate
  medical_facility: '#3b82f6', // blue
  marine_vessel: '#06b6d4', // cyan
  illustrative: '#fbbf24', // amber/gold
};

/** Ontario Corps base accent (thinner stroke than primary ops icons). */
export const OC_COLOR = '#fbbf24';

/** Response-asset class → Lucide glyph (§3). illustrative_marker resolves its
 *  glyph per Ontario Corps category instead (see OC_CATEGORY_ICON). */
export const ASSET_CLASS_ICON: Record<string, string> = {
  fire_station: 'building-2',
  wildfire_base: 'flame',
  aviation_base: 'plane',
  marine_vessel: 'anchor',
  medical_facility: 'stethoscope',
  volunteer_chapter: 'users',
  utility_response: 'zap',
  specialty_team: 'activity',
  umbrella_org: 'building',
  illustrative_marker: 'circle',
};

/** Ontario Corps capability category → Lucide glyph (§4.3). */
export const OC_CATEGORY_ICON: Record<string, string> = {
  search_rescue: 'users',
  disaster_response: 'shield-alert',
  medical_response: 'stethoscope',
  shelter_welfare: 'home',
  heavy_rescue: 'wrench',
  supplies_logistics: 'package',
  indigenous_coord: 'feather',
  communications: 'radio',
  business_coord: 'briefcase',
};

/** Human labels for the OC category badge (AssetMedia §7) + LayerToggle (§4.4). */
export const OC_CATEGORY_LABEL: Record<string, string> = {
  search_rescue: 'Search & Rescue',
  disaster_response: 'Disaster Response',
  medical_response: 'Medical Response',
  shelter_welfare: 'Shelter & Welfare',
  heavy_rescue: 'Heavy & Specialty Rescue',
  supplies_logistics: 'Supplies & Logistics',
  indigenous_coord: 'Indigenous Coordination',
  communications: 'Communications & Info',
  business_coord: 'Business Coordination',
};

// --- Rasterisation + cache --------------------------------------------------

export interface RenderOptions {
  color?: string;
  /** Output pixel size of the (square) glyph. */
  size?: number;
  strokeWidth?: number;
  /** Clockwise degrees; baked into the raster (the PNG IS rotated). */
  rotation?: number;
  /** CAHS glyphs only: force monochrome / gov-compliance ink, overriding the
   *  live {@link cahsRenderMode} flag. Ignored by Lucide / custom glyphs. */
  mono?: boolean;
  /** CAHS glyphs only (#384): paint a contrasting outline BEHIND the fill
   *  (paint-order:stroke) so the glyph reads on mid-tone lifecycle recolours,
   *  not just the dominant URGENT-RED. `color` is a literal (untouched by the
   *  `currentColor`→ink retint); `width` is in the glyph's 0–100 viewBox units.
   *  Ignored by Lucide / custom glyphs. */
  outline?: { color: string; width: number };
}

interface ResolvedOptions {
  color: string;
  size: number;
  strokeWidth: number;
  rotation: number;
}

const DEFAULTS: ResolvedOptions = {
  color: '#e2e8f0',
  size: ICON_SIZES.regional,
  strokeWidth: STROKE_WIDTH,
  rotation: 0,
};

/** Cap on cached entries — bounded so heading-rounded aircraft glyphs (~72 per
 *  kind/role) can't grow unbounded. LRU eviction past this. */
export const MAX_CACHE_ENTRIES = 200;

function cacheKey(name: string, o: ResolvedOptions): string {
  return `${name}|${o.color}|${o.size}|${o.strokeWidth}|${o.rotation}`;
}

function drawNode(ctx: CanvasRenderingContext2D, node: IconNode): void {
  const [tag, a] = node;
  const num = (v: string | number | undefined, d = 0): number =>
    v === undefined ? d : typeof v === 'number' ? v : parseFloat(v);
  switch (tag) {
    case 'path': {
      ctx.stroke(new Path2D(String(a.d)));
      break;
    }
    case 'line': {
      ctx.beginPath();
      ctx.moveTo(num(a.x1), num(a.y1));
      ctx.lineTo(num(a.x2), num(a.y2));
      ctx.stroke();
      break;
    }
    case 'circle': {
      ctx.beginPath();
      ctx.arc(num(a.cx), num(a.cy), num(a.r), 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    case 'ellipse': {
      ctx.beginPath();
      ctx.ellipse(num(a.cx), num(a.cy), num(a.rx), num(a.ry), 0, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    case 'rect': {
      const x = num(a.x);
      const y = num(a.y);
      const w = num(a.width);
      const h = num(a.height);
      const r = num(a.rx, num(a.ry, 0));
      ctx.beginPath();
      if (r > 0 && typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, h, r);
      else ctx.rect(x, y, w, h);
      ctx.stroke();
      break;
    }
    case 'polyline':
    case 'polygon': {
      const pts = String(a.points)
        .trim()
        .split(/[\s,]+/)
        .map(Number);
      ctx.beginPath();
      for (let i = 0; i + 1 < pts.length; i += 2) {
        if (i === 0) ctx.moveTo(pts[i]!, pts[i + 1]!);
        else ctx.lineTo(pts[i]!, pts[i + 1]!);
      }
      if (tag === 'polygon') ctx.closePath();
      ctx.stroke();
      break;
    }
    default:
      break;
  }
}

/** Default canvas rasteriser. Returns null when no canvas is available (jsdom /
 *  happy-dom under unit tests) or the icon is unknown — callers fall back. */
function rasterizeWithCanvas(name: string, o: ResolvedOptions): string | null {
  const nodes = lookupNodes(name);
  if (!nodes) return null;
  if (typeof document === 'undefined') return null;
  const px = o.size * 2; // 2× for crispness; billboard displays at o.size
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.save();
  if (o.rotation) {
    ctx.translate(px / 2, px / 2);
    ctx.rotate((o.rotation * Math.PI) / 180);
    ctx.translate(-px / 2, -px / 2);
  }
  const scale = px / 24; // Lucide viewBox is 24×24
  ctx.scale(scale, scale);
  ctx.strokeStyle = o.color;
  ctx.lineWidth = o.strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const node of nodes) drawNode(ctx, node);
  ctx.restore();
  return canvas.toDataURL('image/png');
}

type Rasterizer = (name: string, o: ResolvedOptions) => string | null;

let rasterizer: Rasterizer = rasterizeWithCanvas;
const cache = new Map<string, string | null>();

/**
 * Render a Lucide (or custom) glyph to a PNG data-URL, cached by every input.
 * Returns null when the glyph can't be rasterised (no canvas / unknown name);
 * callers fall back to a coloured point. Synchronous after first render.
 */
export function renderLucideToDataUrl(name: string, options: RenderOptions = {}): string | null {
  // CAHS glyphs (`cahs:` namespace) take the solid-SVG retint path: monochrome
  // `currentColor` source → family token (or mono ink) → image/svg+xml data-URL
  // via svgIcon, which caches by name+colour and needs no canvas (so it renders
  // identically in unit tests). This is BEFORE the DEFAULTS merge / Lucide cache
  // so the stroke-only canvas pipeline below is untouched.
  if (name.startsWith(CAHS_GLYPH_PREFIX)) {
    const raw = lookupCahsGlyph(name);
    if (raw === null) return null;
    const ink = resolveCahsInk(options.color ?? DEFAULTS.color, options.mono);
    if (options.outline) {
      // #384 — outlined variant: a distinct cache key (the outline is baked into
      // the markup, so the un-outlined `name` key must not collide with it).
      const { color, width } = options.outline;
      return tintedSvgDataUrl(`${name}|o:${color}:${width}`, withGlyphOutline(raw, color, width), ink);
    }
    return tintedSvgDataUrl(name, raw, ink);
  }
  const o: ResolvedOptions = { ...DEFAULTS, ...options };
  const key = cacheKey(name, o);
  if (cache.has(key)) {
    const hit = cache.get(key)!;
    // LRU touch: re-insert so the freshly-used entry is the newest.
    cache.delete(key);
    cache.set(key, hit);
    return hit;
  }
  const url = rasterizer(name, o);
  cache.set(key, url);
  if (cache.size > MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value as string | undefined;
    if (oldest !== undefined) cache.delete(oldest);
  }
  return url;
}

/** True when a glyph (Lucide, custom, or CAHS) is registered for `name`. */
export function hasIcon(name: string): boolean {
  return lookupCahsGlyph(name) !== null || lookupNodes(name) !== null;
}

// --- Test seams (not for production use) ------------------------------------

/** Inject a deterministic rasteriser + clear the cache. Test-only. */
export function __setRasterizerForTest(fn: Rasterizer): void {
  rasterizer = fn;
  cache.clear();
}

/** Restore the canvas rasteriser + clear the cache. Test-only. */
export function __resetRasterizer(): void {
  rasterizer = rasterizeWithCanvas;
  cache.clear();
}

/** Current cache size. Test-only. */
export function __cacheSize(): number {
  return cache.size;
}
